import { sortedLevels } from "building-generation/elements/level/levelTools";
import { roofRidgeVentOutlines } from "building-generation/elements/roofs";
import { type Annotation, type LinearDimension } from "building-types/annotation/annotationTypes";
import type { Building } from "building-types/building/buildingTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { Dimension } from "building-types/dimensions/dimensionTypes";
import type { LevelHandle, RoofHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import {
  roofOverhangDimensionCategoryReplaceMap,
  SheetGeometryCategory,
} from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry, SheetGeometryList } from "building-types/views/sheetGeometryTypes";
import {
  PlanRenderingMode,
  sheetAddGeometries,
  sheetAddGeometry,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import type { DimensionPrecision } from "building-types/views/viewTypes";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { SVG_TO_PDF_SCALE } from "common/constants/pdfSettings";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { degreeToRadian, radianToDegree } from "common/utils/math";
import { numMul, percentNew, sheetPixelsToInches } from "common/utils/nominalNumbers";
import { PlacementXAnchor, PlacementYAnchor } from "common/utils/textAlignment";
import {
  dimensionNew,
  getDimLabel,
  getDimLabelPosition,
  getOrientedDimLine,
  makeDimensionLeader,
} from "doc/dimensions";
import { planAddAnnotation } from "doc/plans/planAnnotations";
import { makeSheetGeometryFromDimensions } from "doc/plans/planDimensions";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { PlanGeometryBuilder } from "doc/plans/planGeometrySource";
import type { PlanGeometryGeneratorResult } from "doc/plans/planGeometrySourceTypes";
import { planDefaultCategoryStyles } from "doc/plans/planStyleSheets";
import { resolveSheetGeometryStyle } from "doc/plans/primitives/primitiveStyles";
import type { SheetGeometryCategoryStyleSheets } from "doc/plans/SheetGeometrySourceStyleOverrideTable";
import type { TransformWithComponents } from "doc/plans/sheetGeometryTools";
import { sheetGeometryTransform } from "doc/plans/sheetGeometryTools";
import { linearDimensionFromThreePoints, linearDimensionToPolyLine2sWithTicks } from "doc/utils/annotation";
import { entityContainsBuiltInLabels } from "doc/utils/builtInLabelUtils";
import { entitiesContainMatchingLabel } from "doc/utils/labelTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { addPolygonsToBSP, buildEdgePolygon, isPointInsideBSP } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { line2EvalOffset, line2Normal } from "geom/line2";
import { mat3Mult, mat3Scale, mat3Translate } from "geom/mat3";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2OnPlane } from "geom/plane";
import {
  polyLine2ByTwoPoints,
  polyLine2Compare,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeCount, polyLine3EdgeEnd, polyLine3EdgeStart } from "geom/polyLine3";
import { vec2Add, vec2AngleBetweenCW, vec2Normalize, vec2Scale } from "geom/vec2";
import { baseStyleDefault } from "lib/house/annotation/svgSettings";
import { downwardSlopeVectorToRoofSlopeFormat } from "lib/house/annotation/util";
import { evalRoofAnnotationSetting, getRoofAnnotationSettings } from "lib/house/plans/planSettingsDefault";
import type { RoofSlopeAnnoInfo } from "lib/house/plans/roof";
import {
  extractRoofPlanOverhangDimInfo,
  extractRoofPlanPerimsAnnoInfo,
  extractRoofPlanRidgeVentAnnoInfo,
  extractRoofPlanSkylightAnnoInfo,
  getSlopeAnnos,
} from "lib/house/plans/roof";
import { generateSlopeTagSheetGeometries } from "lib/house/plans/roofSlopeAnnotation";
import { planSettingsGetFontSizes } from "lib/house/settingsTypes/planSettingsUtils";
import { planAddCeilingsByPredicate } from "./planCeilings";

type RoofPlanGenerationContext = Omit<PlanGenerationContext, "levelId">;

export interface PlanRoofProps {
  readonly mode: PlanRenderingMode;
  readonly building: Building;

  readonly planRotationRads: number;
  readonly labels?: {};
  readonly useBackgroundAnnotationOnly?: boolean;
  readonly useLvlPerims?: boolean;
  readonly noSettings?: boolean;
}

export function makeSheetGeometryFromRoofPerims(
  context: Pick<PlanGenerationContext, "building" | "noSettings">,
): SheetGeometry[] {
  return [...makeSheetGeometryFromRoofPerimsInternal(context), ...makeSheetGeometryFromDormerPerimeters(context)];
}

export function makeSheetGeometryFromRoofPerimsWithLevelPerims(context: PlanGenerationContext) {
  return makeSheetGeometryFromRoofPerimsInternal(context, true);
}

function makeSheetGeometryFromRoofPerimsInternal(
  { building }: Pick<PlanGenerationContext, "building">,
  useLvlPerims?: boolean,
): SheetGeometry[] {
  const { uncoveredPerims, roofLevelPerims } = extractRoofPlanPerimsAnnoInfo(building);
  const roofGeometries: SheetGeometry[] = [];

  for (const level of sortedLevels(building.levels)) {
    const levelId = level.id;
    const levelHandle: LevelHandle = {
      type: "LEVEL",
      id: levelId,
    };

    // TODO: this could be accomplished with a subtraction
    // we cover up any uncovered perimeters below with the perimeters of the roof
    const levelRoofPerimeters = roofLevelPerims[levelId];
    if (useLvlPerims && levelRoofPerimeters) {
      roofGeometries.push(
        sheetPolylineGeometryNew({
          handle: levelHandle,
          paths: levelRoofPerimeters,
          linewidth: ZERO_POINTS,
          fill: true,
          fillColor: "white",
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(0),
        }),
      );
    }

    // then render the uncovered perimeters for this level.
    const levelUncoveredPerimeters = uncoveredPerims[levelId];
    if (levelUncoveredPerimeters) {
      for (const uncoveredPerim of levelUncoveredPerimeters) {
        roofGeometries.push(
          sheetPolylineGeometryNew({
            handle: levelHandle,
            paths: [uncoveredPerim],
            dashMask: true,
          }),
        );
      }
    }
  }

  return roofGeometries;
}

/** @deprecated use PlanGeometrySource instead */
export function planAddRoofs(
  sheetGeometryList: SheetGeometryList,
  { building, planRotationRads, mode, labels, useBackgroundAnnotationOnly, noSettings }: PlanRoofProps,
) {
  const { settings } = building;
  const { planset: planSettings } = settings;
  const { annotationScale } = planSettings;

  sheetAddGeometries(
    sheetGeometryList,
    makeSheetGeometryFromRoofPerimsInternal({ building }, mode === PlanRenderingMode.PDF),
  );

  let filterRoofPlan = (x: Annotation) => entityContainsBuiltInLabels(x, "addToRoofPlan");

  if (useBackgroundAnnotationOnly) {
    filterRoofPlan = (x) => entitiesContainMatchingLabel(x, { labels: labels ?? {} });
  }

  const roofAnnotation: Annotation[] = [];
  const { roomAnnotation, ceilings } = building;
  for (const roomId in roomAnnotation) {
    const linework = roomAnnotation[roomId].filter(filterRoofPlan); // Filters the manual annotations
    roofAnnotation.push(...linework);
  }

  for (const annotation of roofAnnotation) {
    planAddAnnotation(sheetGeometryList, {
      annotation: [annotation],
      assetCatalog: building.doc.assets,
    });
  }

  planAddCeilingsByPredicate(sheetGeometryList, {
    handle: null,
    ceilings,
    subCeilingPredicate: (x) => entityContainsBuiltInLabels(x, "addToRoofPlan"),
  });

  if (!useBackgroundAnnotationOnly) {
    planAddRoofAnnotation(sheetGeometryList, {
      building,
      planRotationRads,
      svgTextScale: annotationScale,
      noSettings,
    });
  }
  return sheetGeometryList;
}

function sheetAddStyledGeometries(
  output: SheetGeometryList,
  input: SheetGeometry[],
  category: SheetGeometryCategory,
  styleSheet: SheetGeometryCategoryStyleSheets = planDefaultCategoryStyles,
) {
  for (const geometry of input) {
    sheetAddGeometry(output, resolveSheetGeometryStyle(geometry, styleSheet[category] ?? {}));
  }
}

function sheetAddStyledGeometriesFromBuilder(
  output: SheetGeometryList,
  input: PlanGeometryGeneratorResult,
  styleSheet?: SheetGeometryCategoryStyleSheets,
) {
  for (const category in input.taggedGeometries) {
    sheetAddStyledGeometries(output, input.taggedGeometries[category], category as SheetGeometryCategory, styleSheet);
  }
}

/** @deprecated use PlanGeometrySource instead */
export function planAddRoofAnnotation(sheetGeometryList: SheetGeometryList, ctx: RoofPlanGenerationContext) {
  sheetAddGeometries(sheetGeometryList, makeSheetGeometryFromRidgeVentAnnos(ctx));
  sheetAddGeometries(sheetGeometryList, makeSheetGeometryFromRoofSlopeAnnos(ctx));
  sheetAddGeometries(sheetGeometryList, makeSheetGeometryFromSkylightAnnos(ctx));
  sheetAddStyledGeometriesFromBuilder(sheetGeometryList, makeSheetGeometryFromRoofOverhangDimensions(ctx));
  sheetAddStyledGeometries(
    sheetGeometryList,
    makeSheetGeometryFromDormerFraming(ctx),
    SheetGeometryCategory.ROOF_DORMER_FRAMING,
  );
}

export function makeSheetGeometryFromRoofRidgeVents(
  props: Omit<PlanRoofProps, "mode" | "planRotationRads">,
): SheetGeometry[] {
  const { building } = props;

  const ridgeVents2D = roofRidgeVentOutlines(building);
  const geometry = sheetPolylineGeometryNew({
    paths: ridgeVents2D,
  });

  return [geometry];
}

export function makeSheetGeometryFromRidgeVentAnnos({
  building,
  noSettings,
}: Pick<PlanGenerationContext, "building" | "noSettings">): SheetGeometry[] {
  if (!noSettings && !building.settings.roof.includeRidgeVent) {
    return [];
  }

  const roofRidgeVentAnnoInfos = extractRoofPlanRidgeVentAnnoInfo(building);

  let fontSize: SheetInches | undefined;
  if (!noSettings) {
    fontSize = planSettingsGetFontSizes(building.settings.planset).regular;
  }

  const sgOut: SheetGeometry[] = [];

  for (const { annoCenterPt, rotationRads, roofHandle } of roofRidgeVentAnnoInfos) {
    const [cX, cY] = annoCenterPt;

    sgOut.push(
      sheetTextGeometryNew({
        handle: roofHandle,
        text: "RIDGE VENT",
        fontSize,
        x: cX,
        y: cY,
        rotation: rotationRads,
      }),
    );
  }

  return sgOut;
}

export function makeSheetGeometryFromSkylightAnnos({
  building,
  planRotationRads,
  noSettings,
}: Pick<PlanGenerationContext, "building" | "planRotationRads" | "noSettings">): SheetGeometry[] {
  const sgOut: SheetGeometry[] = [];

  const roofSkylightAnnoInfos = extractRoofPlanSkylightAnnoInfo(building);

  let fontSize: SheetInches | undefined;

  if (!noSettings) {
    fontSize = sheetPixelsToInches(
      numMul(baseStyleDefault.regularFontSize, building.settings.planset.annotationScale * SVG_TO_PDF_SCALE),
    );
  }

  for (const { annoCenterPt, rotationRads, text, roofHandle } of roofSkylightAnnoInfos) {
    const [cX, cY] = annoCenterPt;
    const rotate = radianToDegree(rotationRads);
    const textRotate = degreeToRadian(textRightSideUp(rotate + 90, planRotationRads) + 90);

    sgOut.push(
      sheetTextGeometryNew({
        handle: roofHandle,
        text,
        fontSize,
        x: cX,
        y: cY,
        rotation: textRotate,
        placementXAnchor: PlacementXAnchor.CENTER,
        placementYAnchor: PlacementYAnchor.BOTTOM,
      }),
    );
  }

  return sgOut;
}

function makeRoofSlopeAnnoGeometries({
  roofSlopeAnnoInfos,
  planRotationRads,
}: {
  roofSlopeAnnoInfos: RoofSlopeAnnoInfo[];
  planRotationRads: number;
}): SheetGeometry[] {
  const sgOut: SheetGeometry[] = [];
  for (const anno of roofSlopeAnnoInfos) {
    const [x, y] = anno.annoCenterPt;
    const unitVec = vec2Normalize(anno.roofSlopeVector[0], anno.roofSlopeVector[1]);
    const scaledVec = vec2Scale(unitVec[0], unitVec[1], 0.75);
    const [vecX, vecY] = scaledVec;
    const rotate = radianToDegree(vec2AngleBetweenCW(0, -1, vecX, vecY));
    const tempGeoList = generateSlopeTagSheetGeometries({
      rotate,
      planRotationRads,
      slope: downwardSlopeVectorToRoofSlopeFormat(anno.roofSlopeVector),
      simplified: anno.simplified,
    });

    // -y scale matches renderPlanImageGeometry scale.setY(-1).
    const transformWithComponents: TransformWithComponents = {
      transform: mat3Mult(mat3Translate(x, y), mat3Scale(1, -1)),
      transformScale: [1, 1],
      transformRotation: 0,
    };

    for (const sheetGeometry of tempGeoList) {
      sgOut.push(sheetGeometryTransform(sheetGeometry, transformWithComponents));
    }
  }

  return sgOut;
}

/** Produces annotations for both roofs and dormers */
export function makeSheetGeometryFromRoofSlopeAnnos({
  building,
  planRotationRads,
  noSettings,
}: Pick<RoofPlanGenerationContext, "building" | "planRotationRads" | "noSettings">): SheetGeometry[] {
  const roofSlopeAnnoInfos = getSlopeAnnos(building, false, noSettings);
  return makeRoofSlopeAnnoGeometries({ roofSlopeAnnoInfos, planRotationRads });
}

function forEachDormer(building: Building, callback: (roof: GeneratedRoof, handle: RoofHandle) => void) {
  for (const roofSytemId in building.roofSystems) {
    const roofSystem = building.roofSystems[roofSytemId];

    for (const roofId in roofSystem.roofs) {
      const roof = roofSystem.roofs[roofId];
      const { isDormer, polygonId, roofVolume } = roof;
      const { hasIndexedPeakDirection } = roofVolume;

      // Skip non-dormer like roofs
      // polygonId < 0 means it's a roof fully contained within another roof and we treat it as a dormer
      // We also exclude roofs with indexed peak direction. These are having a negative polygonId,
      // but we don't want to expose their overhang dimensions as dormers
      const isDormerLikeRoof = isDormer || (polygonId < 0 && !hasIndexedPeakDirection);
      if (!isDormerLikeRoof) {
        continue;
      }

      const handle: RoofHandle = {
        type: "ROOF",
        id: roofId,
      };

      callback(roof, handle);
    }
  }
}

interface RoofOverhangAnnoProps {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  dimensionPrecision?: DimensionPrecision;
}

export function makeRoofOverhangDimensions(props: RoofOverhangAnnoProps): PlanGeometryGeneratorResult {
  const { x1, y1, x2, y2, dimensionPrecision } = props;
  const [labelPosX, labelPosY] = line2EvalOffset(x1, y1, x2, y2, -12, false, -18);

  const dimensions: Dimension[] = [];

  const dimLine = polyLine2ByTwoPoints(x1, y1, x2, y2);

  const placementXAnchor = polyLine2Compare(getOrientedDimLine(dimLine), dimLine)
    ? PlacementXAnchor.RIGHT
    : PlacementXAnchor.LEFT;

  const [textDirX, textDirY] = placementXAnchor === PlacementXAnchor.LEFT ? [-1, 0] : [1, 0];

  makeDimensionLeader(dimensions, {
    pl: dimLine,
    placementXAnchor,
    textDirX,
    textDirY,
    labelPosX,
    labelPosY,
    dimensionPrecision,
  });

  return makeSheetGeometryFromDimensions({
    dimensions,
    useRoofOverhangRenderOrder: true,
  });
}

export function makeSheetGeometryFromRoofOverhangDimensions({
  building,
  dimensionPrecision,
  noSettings,
}: Pick<RoofPlanGenerationContext, "building" | "dimensionPrecision" | "noSettings">): PlanGeometryGeneratorResult {
  const roofOverhangAnnoInfos = extractRoofPlanOverhangDimInfo(building, noSettings);

  const builder = new PlanGeometryBuilder();

  for (const {
    roofOverhangPt: [x1, y1],
    blockPerimContactPt: [x2, y2],
  } of roofOverhangAnnoInfos) {
    const dimensionSheetGeometries = makeRoofOverhangDimensions({ x1, y1, x2, y2, dimensionPrecision });

    builder.addAllFrom(dimensionSheetGeometries, roofOverhangDimensionCategoryReplaceMap);
  }

  builder.addAllFrom(makeSheetGeometryFromDormerOverhangDimensions({ building, noSettings }));

  return builder;
}

// TODO: update this to apply dimension precision
export function makeSheetGeometryFromDormerOverhangDimensions({
  building,
  noSettings,
}: Pick<RoofPlanGenerationContext, "building" | "noSettings">): PlanGeometryGeneratorResult {
  const builder = new PlanGeometryBuilder();

  const {
    settings: { wall: wallSettings, planset },
    roofSystems,
  } = building;

  const planSettings = getRoofAnnotationSettings(planset, noSettings);

  forEachDormer(building, (roof, handle) => {
    if (!evalRoofAnnotationSetting(planSettings, roof.roofPlanSettings, "includeDormerOverhangDimensions")) {
      return;
    }

    const roofSystem = roofSystems[roof.roofSystemId];
    if (!roofSystem) {
      return;
    }

    const { clipContext } = roofSystem;

    const dimensions = makeDormerOverhangDimensions({
      subRoof: roof,
      wallSettings,
      clipContext,
    });

    const dimensionGeometries = makeSheetGeometryFromDimensions({
      handle,
      dimensions,
    });

    builder.addAllFrom(dimensionGeometries, roofOverhangDimensionCategoryReplaceMap);
  });

  return builder;
}

/**
 * @deprecated
 * Dormer-specific framing geometry is strictly limited to the built in plan set.
 */
export function makeSheetGeometryFromDormerFraming({
  building,
}: Pick<RoofPlanGenerationContext, "building">): SheetGeometry[] {
  // The planset toggle gates this in every context; per-roof overrides win (SDO-4342).
  const planSettings = building.settings.planset;

  return makeSheetGeometryFromDormerPerimeters({ building }, (roof) =>
    evalRoofAnnotationSetting(planSettings, roof.roofPlanSettings, "includeDormerDashedFraming"),
  );
}

function makeSheetGeometryFromDormerPerimeters(
  { building }: Pick<RoofPlanGenerationContext, "building">,
  includeRoof: (roof: GeneratedRoof) => boolean = () => true,
): SheetGeometry[] {
  const result: SheetGeometry[] = [];

  const {
    settings: { wall: wallSettings },
  } = building;

  forEachDormer(building, (roof, handle) => {
    if (!includeRoof(roof)) {
      return;
    }

    const dashedFraming = extractDormerDashedFraming({
      subRoof: roof,
      wallSettings,
    });

    if (dashedFraming.length > 0) {
      result.push(
        sheetPolylineGeometryNew({
          handle,
          paths: dashedFraming,
        }),
      );
    }
  });

  return result;
}

function buildRoofTopBSP({ roofTopFaces }: GeneratedRoof): NodeBSP | null {
  let roofTopBSP: NodeBSP | null = null;

  // iterate roof top faces
  for (const { face } of roofTopFaces) {
    const { plane, loops } = face;

    if (!plane || !loops?.length || !loops[0]) {
      continue;
    }

    // iterate the face outer loop edges
    const polygons: PolygonBSP[] = [];
    const perimeter3 = polyLine2OnPlane(plane, loops[0]);
    const perimCount = perimeter3 ? polyLine3EdgeCount(perimeter3) : 0;
    for (let i = 0; i < perimCount; i++) {
      const [x0, y0] = polyLine3EdgeStart(perimeter3, i);
      const [x1, y1] = polyLine3EdgeEnd(perimeter3, i);

      // build the edge polygon
      const polygon = buildEdgePolygon(x0, y0, 0, x1, y1, 0, 0, 0, -1);

      if (!polygon) {
        continue;
      }

      polygons.push(polygon);
    }

    // add the edge polygons from this face to the BSP
    roofTopBSP = addPolygonsToBSP(roofTopBSP, null, polygons, MULTIGABLE_BSP_EPSILON);
  }

  return roofTopBSP;
}

interface ExtractDormerOverhangAnnosOptions {
  readonly subRoof: GeneratedRoof;
  readonly wallSettings: WallSettings;
  readonly clipContext: MultiGableRoofClipContext;
}

function makeDormerOverhangDimensions({
  subRoof,
  wallSettings,
  clipContext,
}: ExtractDormerOverhangAnnosOptions): Dimension[] {
  const {
    roofVolume: { edges, extensions, shedExtensions, roofType },
    roofSettings: { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING },
  } = subRoof;

  const { discardBSP } = clipContext;

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(wallSettings, overhangDatum);

  const dimensions: Dimension[] = [];

  // helper function for adding a dimension
  const addDimension = (dim: LinearDimension) => {
    const { lengthEdges: dimPls, witnesses, ticks: tickPls } = linearDimensionToPolyLine2sWithTicks(dim);

    const dimLine = polyLine2ByTwoPoints(dim.lineStartX, dim.lineStartY, dim.lineEndX, dim.lineEndY);
    const label = getDimLabel(dimLine);
    const labelUnstacked = getDimLabel(dimLine, undefined, false);
    const [midX, midY] = getDimLabelPosition(dimLine);

    const dimension = dimensionNew({
      baseDimLine: dimLine,
      dimLine,
      label,
      labelUnstacked,
      linework: dimPls,
      witnessPls: witnesses,
      ticks: tickPls,
      labelMidPtX: midX,
      labelMidPtY: midY,
      startAnchor: [dim.startX, dim.startY],
      endAnchor: [dim.endX, dim.endY],
    });

    dimensions.push(dimension);
  };

  // helper function for adding an overhang dimension
  const addOverhangDimension = (
    x: number,
    y: number,
    tx: number,
    ty: number,
    overhang: number,
    offset: number,
    isEnd: boolean,
  ) => {
    if (overhang - exteriorWallOffsetWithSheathing < DIST_EPSILON) {
      return;
    }

    const startX = isEnd ? x + tx * exteriorWallOffsetWithSheathing : x - tx * overhang;
    const startY = isEnd ? y + ty * exteriorWallOffsetWithSheathing : y - ty * overhang;

    const endX = isEnd ? x + tx * overhang : x - tx * exteriorWallOffsetWithSheathing;
    const endY = isEnd ? y + ty * overhang : y - ty * exteriorWallOffsetWithSheathing;

    const [nx, ny] = line2Normal(startX, startY, endX, endY);

    addDimension(
      linearDimensionFromThreePoints({
        startX,
        startY,
        endX,
        endY,
        offsetX: startX + nx * offset,
        offsetY: startY + ny * offset,
      }),
    );
  };

  // helper function for adding the overhang dimensions for a single edge
  const addEdgeOverhangDimensions = (edgeInfo: RoofEdge | undefined) => {
    if (!edgeInfo) {
      return;
    }

    const { edge, outerEdge, sideOverhang, startOverhang, endOverhang, startHeight, endHeight } = edgeInfo;
    const [startX, startY] = polyLine2Start(edge);
    const [endX, endY] = polyLine2End(edge);
    const [tx, ty] = polyLine2EdgeTangent(edge, 0);
    const [nx, ny] = polyLine2EdgeNormal(edge, 0);
    const baseOffset = 10;

    // Generate the reference points for visibility check. We apply a normal offset to the reference points,
    // to keep adding these dimensions when the corner point is free, but removing it when we are inside another roof.
    const offset = 1;
    const offsetX = nx * offset;
    const offsetY = ny * offset;
    const [outerStartX, outerStartY] = polyLine2Start(outerEdge);
    const [outerEndX, outerEndY] = polyLine2End(outerEdge);
    const [refStartX, refStartY] = vec2Add(outerStartX, outerStartY, offsetX, offsetY);
    const [refEndX, refEndY] = vec2Add(outerEndX, outerEndY, offsetX, offsetY);

    // Visibility check with the roof volume that includes the overhang
    const isStartInside = isPointInsideBSP(discardBSP, refStartX, refStartY, startHeight - GABLE_ROOF_THICKNESS, false);
    const isEndInside = isPointInsideBSP(discardBSP, refEndX, refEndY, endHeight - GABLE_ROOF_THICKNESS, false);

    // If the end point is outside the roof volume, add the overhang dimensions
    if (!isEndInside) {
      addOverhangDimension(endX, endY, tx, ty, endOverhang, baseOffset + sideOverhang, true);
      addOverhangDimension(endX, endY, -nx, -ny, sideOverhang, baseOffset + endOverhang, false);
    }

    // If the start point is outside the roof volume, add the overhang dimension
    if (!isStartInside) {
      addOverhangDimension(startX, startY, tx, ty, startOverhang, baseOffset + sideOverhang, false);

      // If that side overhang dimension has not been added by the end point check, add it now
      if (isEndInside) {
        addOverhangDimension(startX, startY, nx, ny, sideOverhang, baseOffset + startOverhang, true);
      }
    }
  };

  if (roofType === GableRoofType.DEFAULT) {
    // handle gable roofs
    const startEdge = edges.find((edgeInfo) => edgeInfo.side === RoofSide.START);
    const endEdge = edges.find((edgeInfo) => edgeInfo.side === RoofSide.END);
    const { hasStartExtension, hasEndExtension } = extensions;

    // handle the start edge
    if (startEdge && !hasStartExtension) {
      addEdgeOverhangDimensions(startEdge);
    }

    // handle the end edge
    if (endEdge && !hasEndExtension) {
      addEdgeOverhangDimensions(endEdge);
    }
  } else {
    // handle shed roofs
    const leftEdge = edges.find((edgeInfo) => edgeInfo.side === RoofSide.LEFT);
    const rightEdge = edges.find((edgeInfo) => edgeInfo.side === RoofSide.RIGHT);
    const { hasLeftExtension, hasRightExtension } = shedExtensions;

    // if the left edge is present and not connected to a parent roof, add the overhang dimensions
    if (leftEdge && !hasLeftExtension) {
      addEdgeOverhangDimensions(leftEdge);
    }

    // if the right edge is present and not connected to a parent roof, add the overhang dimensions
    if (rightEdge && !hasRightExtension) {
      addEdgeOverhangDimensions(rightEdge);
    }
  }

  return dimensions;
}

interface ExtractDormerDashedFramingOptions {
  readonly subRoof: GeneratedRoof;
  readonly wallSettings: WallSettings;
}

function extractDormerDashedFraming({ subRoof, wallSettings }: ExtractDormerDashedFramingOptions): PolyLine2[] {
  const {
    roofVolume: { exactEdges, extensions, baseHeight, shedExtensions },
    roofPerimeterRails,
    roofSettings: { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING },
  } = subRoof;
  const { hasStartExtension, hasEndExtension } = extensions;
  const { hasLeftExtension, hasRightExtension } = shedExtensions;

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(wallSettings, overhangDatum);

  // build the clipping volume
  const roofTopBSP: NodeBSP | null = buildRoofTopBSP(subRoof);

  // clipping options
  const options: ClipRailContext = {
    clippingNodes: [clippingNodeNew(roofTopBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // iterate edges
  const dashedFraming: PolyLine2[] = [];
  for (const roofPerimeterRail of roofPerimeterRails) {
    const { edgeIndex, wallLimitsRail } = roofPerimeterRail;
    const { isStartEdge, isEndEdge, isLeftEdge, isRightEdge } = exactEdges[edgeIndex];

    // skip edges that are part of the roof extension
    if (
      (hasStartExtension && isStartEdge) ||
      (hasEndExtension && isEndEdge) ||
      (hasLeftExtension && isLeftEdge) ||
      (hasRightExtension && isRightEdge)
    ) {
      continue;
    }

    const offsetEdge = offsetPolyLine2(wallLimitsRail, exteriorWallOffsetWithSheathing);

    // clip the edge and add the result to the paths
    clipRail({
      rail: offsetEdge,
      height: baseHeight,
      options,
      clippedRails: dashedFraming,
    });
  }

  return dashedFraming;
}

function textRightSideUp(rawAngleCW: number, planRotationRads: number): number {
  const angleCW = (rawAngleCW + radianToDegree(planRotationRads)) % 360;

  //flips the slope text to so that words remain right-side-up
  if (angleCW >= 0 && angleCW < 180) {
    return 270;
  } else {
    return 90;
  }
}
